'use client';

import React, { PropsWithChildren } from 'react';
import { motion } from 'framer-motion';
import { Box } from '@mui/material';
import { DrawerHeader, NavigationDrawer } from '@/components/navigation';
import { useToggle } from '@ianesthesie-kit/core';
import { useAppSelector, useDesktopQuery } from '@/hooks';
import { AppBar } from '@/components/appBar';
import { PaddedWrapper } from '@/components/paddedWrapper';
import { RootState } from '@/store';
import isEmpty from 'lodash/fp/isEmpty';
import { AuthenticatedUserChecks } from './userChecks';

const drawerWidth = 300;

type AuthenticatedLayoutProps = PropsWithChildren<{
  disablePadding?: boolean;
}>;

const selectUserAuthenticated = (state: RootState) =>
  !state.auth.loading &&
  !isEmpty(state.auth.user) &&
  !state.auth.user.locked &&
  state.auth.user.enabled;

export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = (
  props
) => {
  const onDesktop = useDesktopQuery();
  const isUserAuthenticated = useAppSelector(selectUserAuthenticated);
  const {
    toggledOn: drawerOpen,
    toggleOff: closeDrawer,
    toggle: toggleDrawer
  } = useToggle();

  const Wrapper = props.disablePadding ? Box : PaddedWrapper;

  if (!isUserAuthenticated) {
    return null;
  }

  return (
    <>
      {isUserAuthenticated && <AuthenticatedUserChecks />}
      <Box sx={{ display: 'flex' }}>
        <motion.div
          initial={false}
          animate={drawerOpen ? 'open' : 'closed'}
          transition={{
            type: 'tween',
            duration: 0.225
          }}
          variants={{
            open: {
              width: 'fit-content'
            },
            closed: {
              width: 0
            }
          }}
        >
          <AppBar
            drawerWidth={drawerWidth}
            open={onDesktop && drawerOpen}
            onToggleClick={() => toggleDrawer()}
          />
          <NavigationDrawer
            onClose={closeDrawer}
            drawerWidth={drawerWidth}
            open={drawerOpen}
          />
        </motion.div>
        <Box component="main" flexGrow={1}>
          <DrawerHeader />
          <Wrapper>{props.children}</Wrapper>
        </Box>
      </Box>
    </>
  );
};
